/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useFlags } from 'sarsaparilla';

/**
 * **Temporary, for launch only. Remove once the notice has run in production.**
 *
 * Reads the launch kill switch: one LaunchDarkly flag that stops the notice
 * rendering everywhere, without coordinating a deploy across every service that
 * adopted it. It exists because the notice fires on a WAF signal the frontend does
 * not control, and the failure mode — a blocking dialog that reloads the page —
 * lands on legitimate traffic if that signal is wrong.
 *
 * Absent reads as on, so no environment has to create the flag to keep the notice,
 * and a LaunchDarkly outage cannot switch it off. Only an explicit `false` does.
 *
 * The stable way for a team to gate the notice is the `shouldTrigger` predicate on
 * `registerRateLimitInterceptor`, which is per-service, needs no flag, and keeps
 * this module free of flag infrastructure. Delete this hook and its call in
 * `ControlledRateLimitModal` after launch and point teams there — see #5903 and the
 * README's "Launch kill switch" for the whole argument.
 *
 * The flag is read at render rather than when a 429 is detected: detection runs
 * outside React — in an axios interceptor, or a service's own error handling — where
 * there is no LaunchDarkly client to read. Gating the render turns off every path
 * into the notice at once, including the countdown and the reload it arms.
 */
export function useIsRateLimitNoticeEnabled(): boolean {
    const { enableRateLimitNotice } = useFlags();

    return enableRateLimitNotice !== false;
}
